"use client";

import { useEffect } from "react";
import clsx from "clsx";
import Image from "next/image";
import Link from "next/link";
import { ArrowRight, X } from "lucide-react";

import styles from "./MobileMenu.module.css";

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

export function MobileMenu({ isOpen, onClose }: MobileMenuProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  const handleLinkClick = () => {
    onClose();
  };

  return (
    <div className={clsx(styles.mobileMenu, isOpen && styles.mobileMenuActive)}>
      {/* Close Button */}
      <button
        onClick={onClose}
        className={styles.mobileClose}
        aria-label="Закрыть меню"
      >
        <X className="h-5 w-5 text-white" />
      </button>

      {/* Mobile Menu Content */}
      <div className={styles.mobileContent}>
        {/* Logo */}
        <div className={styles.mobileLogo}>
          <Image
            src="/media/photos/logo.png"
            alt="Флагман Строй"
            width={64}
            height={64}
            className="mb-2 h-16 w-16 object-contain"
          />
          <span className="font-display text-3xl font-bold text-white">
            ФЛАГМАН
          </span>
        </div>

        {/* Navigation Links */}
        <nav
          className={styles.mobileNav}
          role="navigation"
          aria-label="Мобильная навигация"
        >
          <Link
            href="#services"
            className={styles.mobileLink}
            onClick={handleLinkClick}
          >
            <span className={styles.linkNumber}>01</span>
            <span className={styles.linkText}>Услуги</span>
            <ArrowRight className={clsx(styles.linkIcon, "h-5 w-5")} />
          </Link>
          <Link
            href="#portfolio"
            className={styles.mobileLink}
            onClick={handleLinkClick}
          >
            <span className={styles.linkNumber}>02</span>
            <span className={styles.linkText}>Портфолио</span>
            <ArrowRight className={clsx(styles.linkIcon, "h-5 w-5")} />
          </Link>
          <Link
            href="#excellence"
            className={styles.mobileLink}
            onClick={handleLinkClick}
          >
            <span className={styles.linkNumber}>03</span>
            <span className={styles.linkText}>Преимущества</span>
            <ArrowRight className={clsx(styles.linkIcon, "h-5 w-5")} />
          </Link>
          <Link
            href="#process"
            className={styles.mobileLink}
            onClick={handleLinkClick}
          >
            <span className={styles.linkNumber}>04</span>
            <span className={styles.linkText}>Процесс</span>
            <ArrowRight className={clsx(styles.linkIcon, "h-5 w-5")} />
          </Link>
          <Link
            href="#contact"
            className={clsx(styles.mobileLink, styles.mobileLinkCta)}
            onClick={handleLinkClick}
          >
            <span className={styles.linkNumber}>05</span>
            <span className={styles.linkText}>Связаться с нами</span>
            <ArrowRight className={clsx(styles.linkIcon, "h-5 w-5")} />
          </Link>
        </nav>

        {/* Contact Info */}
        <div className={styles.mobileContact}>
          <p className="mb-2 text-sm tracking-wider text-white/50 uppercase">
            Свяжитесь с нами
          </p>
          <p className="mb-1 text-lg font-semibold text-white">
            +7 (929) 888-55-51
          </p>
          <p className="text-sm text-white/70">
            С 2010 года в Чеченской Республике
          </p>
        </div>
      </div>

      {/* Decorative Elements */}
      <div className={clsx(styles.mobileDecor, styles.mobileDecor1)} />
      <div className={clsx(styles.mobileDecor, styles.mobileDecor2)} />
    </div>
  );
}
